import { filterPathIntersection, findPathIntersection } from "oc-app/metadata/GraphMetadata";
import { MAuswahlwert, MDetailSerie, MElement, MMass } from "../../metamodell";
import { PAuswahlwert, PDetailSerie, PElement, PObjekt, PTor } from "../../metamodell_paths";
import { ZElement } from "../arrangement/ZElement";
import { ArrangementMass } from "./ArrangementMass";
import { equalsEpsilon } from "oc-commons/Maths";
import { isEmpty } from "oc-commons/Commons";
import * as _ from "lodash";
import {
  elementOrTor,
  getElementMetasFormFüllung,
  getElementMetasGleicherForm,
  getSenkrechtFormVonNormalerForm,
} from "../arrangement/elementFunctions";
import { defaultOf } from "./MHelper";
import { MASSE } from "./Masse";
import { Pfosten } from "../arrangement/Pfosten";
import { updateAnMauer } from "./AnMauerFunctions";
import {
  berechneFundament1Überstand,
  berechneSchiebetorFundamente,
} from "../arrangement3d/schiebetor/schiebetorSkizze";
import { schiebetorKennzahlen } from "../arrangement3d/schiebetor/erzeugeSchiebetorRahmen";

/**
 * Sammlung der Werte, die Konfiguriert werden können.
 * Ergänzt um getter, setter und Hilfsfunktionen, die den Zustand der Konfiguration beschreiben.
 * Wichtigste Methoden: getPassende Auswahlwerte und setAuswahlwert
 */
export class Konfiguration {
  arrangement: ArrangementMass;
  private Workflow: MAuswahlwert;
  private DetailSerie: MAuswahlwert;
  private Position_Einzeltor: MAuswahlwert;
  AnMauer: MAuswahlwert;

  private workflow2Form: {
    WORKFLOW_Einzeltor: MAuswahlwert;
    WORKFLOW_Doppeltor: MAuswahlwert;
    WORKFLOW_Dreifachtor: MAuswahlwert;
    WORKFLOW_Zaunelement: MAuswahlwert;
    WORKFLOW_Doppeltor_EAntrieb: MAuswahlwert;
    WORKFLOW_Schiebetor: MAuswahlwert;
  };

  constructor(arrangement: ArrangementMass) {
    this.arrangement = arrangement;

    const formAuswahlwerte = APP.metadata.Form_Auswahlwert;
    this.workflow2Form = {
      WORKFLOW_Zaunelement: formAuswahlwerte.find(
        (formAuswahlwert: MAuswahlwert) => formAuswahlwert.Key == "Rechteck"
      ),
      WORKFLOW_Einzeltor: formAuswahlwerte.find(
        (formAuswahlwert: MAuswahlwert) => formAuswahlwert.Key == "Einzeltor"
      ),
      WORKFLOW_Doppeltor: formAuswahlwerte.find(
        (formAuswahlwert: MAuswahlwert) => formAuswahlwert.Key == "Doppeltor"
      ),
      WORKFLOW_Doppeltor_EAntrieb: formAuswahlwerte.find(
        (formAuswahlwert: MAuswahlwert) => formAuswahlwert.Key == "Doppeltor"
      ),
      WORKFLOW_Dreifachtor: formAuswahlwerte.find(
        (formAuswahlwert: MAuswahlwert) => formAuswahlwert.Key == "Doppeltor"
      ),
      WORKFLOW_Schiebetor: formAuswahlwerte.find(
        (formAuswahlwert: MAuswahlwert) => formAuswahlwert.Key == "Schiebetor"
      ),
    };
  }

  /**
   * Element in der Szene
   */
  get currentElement(): ZElement {
    return APP.arrangement.getCurrentElement();
  }

  /**
   * Einzeltor-Element in der Szene bei Dreifachtor
   */
  get currentElementEinzeltor(): ZElement {
    return APP.arrangement.getCurrentElementEinzeltor();
  }

  /**
   * Gibt den aktuell ausgewählten Auswahlwert für what zurück
   * @param what Was ist gefragt? "Workflow", "DetailSerie", "Füllung", ...
   */
  getAuswahlwert(what: string): MAuswahlwert {
    if (what == "Mass") {
      return this.currentElement.meta.Mass.Auswahlwert;
    } else if (what == "Profil" && !this.currentElement.showProfil) {
      return null;
    } else if (what == "Senkrechtprofile") {
      return APP.metadata.Senkrechtprofile_Auswahlwert.find(
        (auswahlwert) => auswahlwert.Leeroption !== this.currentElement.isSenkrechtprofile
      );
    } else if (what == "Anschlagprofil") {
      return this.currentElement.anschlagprofilAuswahlwert;
    } else if (what == "Anschlagprofilfarbe") {
      return this.currentElement.getWandmontagePlatten()?.[0].anschlagprofilfarbeAuswahlwert;
    }

    return this[what] || this.arrangement.auswahlAccessoren[what]?.auswahlwert;
  }

  /**
   * Gibt alle noch möglichen Auswahlwerte an. Priorität nach Reihenfolge:
   * 1. Workflow (Form)
   * 2. DetailSerie
   * 3. Füllung
   * ...
   * @param what Was ist gefragt? "Workflow", "DetailSerie", "Füllung", ...
   */
  getPassendeAuswahlwerte(what: String): MAuswahlwert[] {
    let result = [];
    if (what == "Workflow") {
      result = APP.metadata.Workflow_Auswahlwert;
    } else if (what == "DetailSerie") {
      result = this.getDetailSerienAuswahlwerte();
    } else if (what == "Füllung") {
      result = this.getFüllungAuswahlwerte();
    } else if (what == "Senkrechtprofile") {
      result = APP.metadata.Senkrechtprofile_Auswahlwert;
    } else if (what == "Position_Einzeltor") {
      result = APP.metadata.Position_Einzeltor_Auswahlwert;
    } else if (what == "AnMauer") {
      result = this.getAnMauerAuswahlwerte();
    } else if (what == "Anschlagprofil") {
      result = APP.metadata.Anschlagprofil_Auswahlwert;
    } else if (what == "Anschlagprofilfarbe") {
      result = APP.metadata.Anschlagprofilfarbe_Auswahlwert;
    } else if (what == "Mass") {
      result = this.getMassAuswahlwerte();
    } else {
      result = this.arrangement.auswahlAccessoren[what as string]?.möglicheAuswahlwerte || [];
    }
    return result?.filter((auswahlwert) => !auswahlwert.GesperrtMass);
  }

  /**
   * Setzt einen Auswahlwert für die Auswahl what
   * @param what Was soll gesetzt werden? "Workflow", "DetailSerie", "Füllung", ...
   * @param newAuswahlwert Neuer Auswahlwert
   */
  setAuswahlwert(what: String, newAuswahlwert: MAuswahlwert) {
    this.arrangement.setSelectedObjects([this.currentElement]);

    if (what == "Workflow") {
      this.setWorkflow(newAuswahlwert);
    } else if (what == "DetailSerie") {
      this.setDetailSerie(newAuswahlwert);
    } else if (what == "Position_Einzeltor") {
      this.setPosition_Einzeltor(newAuswahlwert);
    } else if (what == "AnMauer") {
      this.setAnMauer(newAuswahlwert);
    } else if (what == "Anschlagprofil") {
      this.currentElement.anschlagprofilAuswahlwert = newAuswahlwert;
      APP.dirty();
    } else if (what == "Anschlagprofilfarbe") {
      this.currentElement
        .getWandmontagePlatten()
        .forEach((p) => (p.anschlagprofilfarbeAuswahlwert = newAuswahlwert));
      APP.dirty();
    } else if (what == "Profil") {
      if (newAuswahlwert == null) {
        this.currentElement.showProfil = false;
        APP.dirty();
      } else {
        this.currentElement.showProfil = true;
        this.arrangement.auswahlAccessoren["Profil"].setter(newAuswahlwert);
      }
    } else if (what == "Senkrechtprofile") {
      this.setSenkrechtprofile(newAuswahlwert);
      if (this.isDreifachtor())
        this.setSenkrechtprofile(newAuswahlwert, this.currentElementEinzeltor);
    } else if (what == "Mass") {
      this.currentElement.change({ massAuswahlwert: newAuswahlwert });
      if (this.isAnMauerBeide()) {
        updateAnMauer(this.currentElement.lichteWeite);
      }

      APP.previewPictures?.resetView();
      APP.dirty();
    } else if (what == "Füllung") {
      const inspiration = newAuswahlwert.Inspiration?.[0];
      if (inspiration) {
        APP.arrangement.inspiration = inspiration;
      }
      this.arrangement.auswahlAccessoren[what as string].setter(newAuswahlwert);
    } else {
      this.arrangement.auswahlAccessoren[what as string].setter(newAuswahlwert);
    }

    if (what == "Öffnung") this.update3fachEinzeltor();

    setTimeout(() => APP.ui?.dirty(), 50);
    if (APP.updateMenuContainerHeight) APP.updateMenuContainerHeight();
  }

  /**
   * Gibt die aktuelle Dimension zurück
   * @param what Welche Dimension
   */
  getMass(what: MASSE): number {
    const element = this.currentElement;
    if (what == MASSE.Breite) {
      return element.breite;
    } else if (what == MASSE.BreiteGehflügel) {
      return element.gehflügelbreite;
    } else if (what == MASSE.BreiteStandflügel) {
      return element.standflügelbreite;
    } else if (what == MASSE.BreiteEinzeltor) {
      return this.currentElementEinzeltor?.breite || 0;
    } else if (what == MASSE.Höhe) {
      return element.höhe;
    } else if (what == MASSE.LichteWeite) {
      return element.lichteWeite;
    } else if (what == MASSE.LichteWeiteEinzeltor) {
      return this.currentElementEinzeltor.lichteWeite;
    } else if (what == MASSE.Platzbedarf) {
      return element.lichteWeite + this.getPfostenBreiteSumme() + this.getEAntriebÜberstand() * 2;
    } else if (what == MASSE.Gesamtbreite) {
      if (this.isDreifachtor()) {
        return (
          element.lichteWeite +
          this.currentElementEinzeltor.lichteWeite +
          this.getPfostenBreiteSumme()
        );
      } else {
        return element.lichteWeite + this.getPfostenBreiteSumme();
      }
    } else if (what == MASSE.PlatzbedarfSchiebetor) {
      const { oeffnungsplatzbedarf } = schiebetorKennzahlen({
        lichteWeite: element.lichteWeite,
        torHöhe: element.size.y,
      });
      return oeffnungsplatzbedarf;
    } else if (what == MASSE.PlatzbedarfSchiebetorInklusiveFundament) {
      const { oeffnungsplatzbedarf } = schiebetorKennzahlen({
        lichteWeite: element.lichteWeite,
        torHöhe: element.size.y,
      });
      const schiebetorFundamentüberstand = this.getSchiebetorFundamentüberstandZusatz();
      return oeffnungsplatzbedarf + schiebetorFundamentüberstand;
    } else if (what == MASSE.PlatzbedarfSchiebetorTiefe) {
      const platzbedarfTiefe = berechneSchiebetorFundamente(element)?.fundament2?.tiefeBrut;
      return platzbedarfTiefe;
    }
  }

  /**
   * Setzt eine Dimension
   * @param what welche Dimension?
   */
  setMass(what: MASSE, newValue: number) {
    if (equalsEpsilon(newValue, this.getMass(what))) return;

    const element = this.currentElement;
    if (what == MASSE.Breite) {
      element.setKonfigurierbareBreite(newValue);
    } else if (what == MASSE.BreiteGehflügel) {
      if (this.isAnMauerBeide()) {
        const delta = element.gehflügelbreite - newValue;
        const newStandflügelbreite = element.standflügelbreite + delta;
        if (newStandflügelbreite < element.sizeRange.flügelBox.max.x + 0.00001) {
          element.standflügelbreite += delta;
          element.gehflügelbreite = newValue;
          element.setSizeFromFlügelBreiten();
        }
      } else {
        element.gehflügelbreite = newValue;
        element.setSizeFromFlügelBreiten();
      }
    } else if (what == MASSE.BreiteStandflügel) {
      if (this.isAnMauerBeide()) {
        const delta = element.standflügelbreite - newValue;
        const newGehflügelbreite = element.gehflügelbreite + delta;
        if (newGehflügelbreite < element.sizeRange.flügelBox.max.x + 0.00001) {
          element.gehflügelbreite += delta;
          element.standflügelbreite = newValue;
          element.setSizeFromFlügelBreiten();
        }
      } else {
        element.standflügelbreite = newValue;
        element.setSizeFromFlügelBreiten();
      }
    } else if (what == MASSE.BreiteEinzeltor) {
      this.currentElementEinzeltor?.setKonfigurierbareBreite(newValue);
    } else if (what == MASSE.LichteWeiteEinzeltor) {
      this.currentElementEinzeltor.setKonfigurierbareBreite(
        newValue - this.getLichteWeiteZusatz(this.currentElementEinzeltor)
      );
    } else if (what == MASSE.Höhe) {
      element.setKonfigurierbareHöhe(newValue);
      this.currentElementEinzeltor?.setKonfigurierbareHöhe(newValue);
    } else if (what == MASSE.LichteWeite) {
      if (this.isAnMauerBeide()) {
        updateAnMauer(newValue);
      } else {
        const flügelBreiteSummeNeu = newValue - this.getLichteWeiteZusatz();
        if (this.isDoppeltor() || this.isDreifachtor()) {
          const flügelBreiteSummeAlt = element.standflügelbreite + element.gehflügelbreite;
          let diffAll = flügelBreiteSummeNeu - flügelBreiteSummeAlt;
          let diff1 = 0;
          let diff2 = 0;

          const minMaxBox = this.currentElement.sizeRange.flügelBox;
          const min = minMaxBox.min.x;
          const max = minMaxBox.max.x;

          if (diffAll > 0) {
            diff1 = Math.min(diffAll, max - this.getMass(MASSE.BreiteGehflügel));
            diffAll -= diff1;
            diff2 = Math.min(diffAll, max - this.getMass(MASSE.BreiteStandflügel));
          } else {
            diff1 = Math.max(diffAll, min - this.getMass(MASSE.BreiteGehflügel));
            diffAll -= diff1;
            diff2 = Math.max(diffAll, min - this.getMass(MASSE.BreiteStandflügel));
          }

          this.setMass(MASSE.BreiteGehflügel, this.getMass(MASSE.BreiteGehflügel) + diff1);
          this.setMass(MASSE.BreiteStandflügel, this.getMass(MASSE.BreiteStandflügel) + diff2);
        } else {
          element.setKonfigurierbareBreite(flügelBreiteSummeNeu);
        }
      }
    } else if (what == MASSE.Gesamtbreite) {
      if (!this.isDreifachtor()) {
        this.setMass(MASSE.LichteWeite, newValue - this.getPfostenBreiteSumme());
      } else {
        let diffAll = newValue - this.getMass(MASSE.Gesamtbreite);
        let diff1 = 0;
        let diff2 = 0;

        const doppelBox = this.currentElement.sizeRange.box;
        const einzelBox = this.currentElementEinzeltor.sizeRange.box;
        const doppelSize = this.currentElement.size.x;
        const einzelSize = this.currentElementEinzeltor.size.x;

        if (diffAll > 0) {
          diff1 = Math.min(diffAll, doppelBox.max.x - doppelSize);
          diffAll -= diff1;
          diff2 = Math.min(diffAll, einzelBox.max.x - einzelSize);
        } else {
          diff1 = Math.max(diffAll, doppelBox.min.x - doppelSize);
          diffAll -= diff1;
          diff2 = Math.max(diffAll, einzelBox.min.x - einzelSize);
        }

        this.setMass(MASSE.LichteWeite, this.getMass(MASSE.LichteWeite) + diff1);
        this.setMass(MASSE.LichteWeiteEinzeltor, this.getMass(MASSE.LichteWeiteEinzeltor) + diff2);
      }
    }

    element.updateSize();
    this.currentElementEinzeltor?.updateSize();

    APP.previewPictures?.resetView();
    APP.dirty();
  }

  /**
   * Setze den Workflow
   * @param newWorkflowAuswahlwert
   */
  setWorkflow(newWorkflowAuswahlwert: MAuswahlwert) {
    // if (this.Workflow === newWorkflowAuswahlwert) return;

    this.Workflow = newWorkflowAuswahlwert;
    const newFormAuswahlwert = this.workflow2Form[newWorkflowAuswahlwert.Key];

    this.setAnMauer(null);

    this.setForm(newFormAuswahlwert);
    APP.arrangement.updateAuswahlAccessoren();
    this.setEAntrieb(newWorkflowAuswahlwert);
    APP.arrangement.updateAuswahlAccessoren();
    const aktuellerDrücker = this.getAuswahlwert("Drücker");
    if (!aktuellerDrücker || aktuellerDrücker.Leeroption) this.setDefault("Drücker");

    this.update3fachEinzeltor();

    APP.previewPictures?.resetView();
    APP.dirty();
  }

  /**
   * Stellt den richtigen Zustand bei Dreifachtoren her:
   * Fügt Einzeltor hinzu oder entfernt es
   * Setzt das Einzeltor an die richtige Position
   * Setzt die Öffnungsrichtung des Einzeltors so, dass kein Fehler entsteht
   * @private
   */
  private update3fachEinzeltor() {
    const segment = this.arrangement.getSegment();
    const elemente = segment.getElemente() as ZElement[];
    if (this.isDreifachtor()) {
      const einzeltorLinksVonKlobenseiteAus =
        this.Position_Einzeltor?.Key === "Einzeltor_3fach_links";
      const isÖffnungNachInnen = this.isÖffnungNachInnen();
      const einzeltorIndex = einzeltorLinksVonKlobenseiteAus == isÖffnungNachInnen ? 1 : 0;
      const einzelTorAnschlag = einzeltorLinksVonKlobenseiteAus
        ? "Anschlag_links"
        : "Anschlag_rechts";

      if (elemente.length < 2) {
        segment.addElemente(2, this.currentElement, 0, 1, -1);
        this.setForm(
          this.workflow2Form["WORKFLOW_Einzeltor"],
          (segment.getElemente() as ZElement[])[einzeltorIndex]
        );
      } else if (!elemente[einzeltorIndex].isEinzeltor) {
        this.arrangement.tauscheBenachbarteElemente(this.arrangement.getElemente());
      }

      this.currentElementEinzeltor.anschlagAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
        einzelTorAnschlag,
        "Anschlag"
      );

      this.currentElementEinzeltor.setKonfigurierbareHöhe(this.currentElement.höhe);
    } else {
      if (elemente.length > 1) {
        segment.remove(this.currentElementEinzeltor);
      }
    }
  }

  /**
   * Gibt den Auswahlwert der Form des aktuellen Elements zurück
   */
  get Form(): MAuswahlwert {
    return this.currentElement.form.Auswahlwert;
  }

  /**
   * Setzt eine gegebene Form indem von allen Massen mit der Form dasjenige gesetzt wird, dass als
   * Standard (Voreinstellung oder erstes in der Sortierung) definiert ist
   * @param newFormAuswahlwert
   * @private
   */
  private setForm(newFormAuswahlwert: MAuswahlwert, element = this.currentElement) {
    if (this.Form === newFormAuswahlwert) return;

    this.setToDefaultMasse(newFormAuswahlwert, element);

    // Wenn DetailSerie nicht mehr in Auswahlen => neu setzen
    const möglicheDetailSerien = this.getPassendeAuswahlwerte("DetailSerie");
    const aktuelleDetailSerie = this.getAuswahlwert("DetailSerie");
    if (!_.includes(möglicheDetailSerien, aktuelleDetailSerie))
      this.setAuswahlwert("DetailSerie", defaultOf(möglicheDetailSerien));

    this.arrangement.getSegment().repositionAndCheckSnaps();
  }

  private setToDefaultMasse(newFormAuswahlwert: MAuswahlwert, element = this.currentElement) {
    const massAuswahlwerte = filterPathIntersection(
      newFormAuswahlwert,
      (p: PAuswahlwert) => p.Form_Objekt.Mass.Auswahlwert
    ) as MAuswahlwert[];
    const massAuswahlwert = defaultOf(massAuswahlwerte);
    element.change({ massAuswahlwert });
  }

  /**
   * Setzt E-Antrieb abhängig vom Workflow
   * @param newWorkflowAuswahlwert
   * @private
   */
  private setEAntrieb(newWorkflowAuswahlwert: MAuswahlwert) {
    const isEAntrieb = newWorkflowAuswahlwert.Key == "WORKFLOW_Doppeltor_EAntrieb";
    const eAntriebKey = isEAntrieb ? "E_Antrieb_mit" : "E_Antrieb_ohne";

    let eAntriebAuswahlwert = this.getPassendeAuswahlwerte("E_Antrieb_Vorhanden").find(
      (aw) => aw.Key == eAntriebKey
    );
    if (isEAntrieb && !eAntriebAuswahlwert) {
      this.setToDefaultMasse(this.workflow2Form[newWorkflowAuswahlwert.Key]);
      eAntriebAuswahlwert = this.getPassendeAuswahlwerte("E_Antrieb_Vorhanden").find(
        (aw) => aw.Key == eAntriebKey
      );
    }

    this.setAuswahlwert("E_Antrieb_Vorhanden", eAntriebAuswahlwert);
  }

  /**
   * Hole alle Auswahlwerte für DetailSerien abhängig vom Workflow
   */
  private getDetailSerienAuswahlwerte(): MAuswahlwert[] {
    const elementeMitForm = (
      filterPathIntersection(this.Form, (p: PAuswahlwert) =>
        elementOrTor(p.Form_Objekt.Mass)
      ) as MElement[]
    ).filter((el) => !el.GesperrtMass);
    const detailSerien = filterPathIntersection(
      elementeMitForm,
      (p: PElement) => p.DetailSerie.Auswahlwert // TODO: Mass3d Hier weiter einschränken durch weiteres Paar für E-Antrieb. Alternativ nur Elemente ausspucken und dann selbst filtern und Pfad langgehen.
    ) as MAuswahlwert[];

    return detailSerien;
  }

  /**
   * Setze DetailSerie. Wird nicht von Accessoren angeboten.
   * Eigentlich wird die Füllung gesetzt, da diese die DetailSerie bestimmt.
   * @param newDetailSerieAuswahlwert
   */
  private setDetailSerie(newDetailSerieAuswahlwert: MAuswahlwert) {
    this.DetailSerie = newDetailSerieAuswahlwert;

    // Serie muss auch passend gesetzt werden, da sich einige AuswahlAccessoren darauf stützen
    APP.arrangement.inspiration = findPathIntersection(
      this.DetailSerie,
      (p: PAuswahlwert) => p.Inspiration
    );

    const füllungenMitFormUndDetailSerie = filterPathIntersection(
      this.Form,
      (p: PAuswahlwert) => elementOrTor(p.Objekt.Mass).Füllung.Auswahlwert,
      newDetailSerieAuswahlwert,
      (p: PAuswahlwert) => p.DetailSerie.Element.Füllung.Auswahlwert
    ) as MAuswahlwert[];

    // Wenn Füllung nicht mehr in Auswahlen => neu setzen
    const aktuelleFüllung = this.getAuswahlwert("Füllung");
    if (!_.includes(füllungenMitFormUndDetailSerie, aktuelleFüllung))
      this.setAuswahlwert("Füllung", defaultOf(füllungenMitFormUndDetailSerie));
  }

  /**
   * Spezieller Code um die Auswahlwerte für Füllungen zu erhalten, da in Accessoren zu viele
   * zurückkommen, da nicht auf DetailSerie eingeschränkt wird
   */
  private getFüllungAuswahlwerte(): MAuswahlwert[] {
    const detailSerieObjekte = filterPathIntersection(
      this.DetailSerie,
      (p: PAuswahlwert) => p.DetailSerie
    ) as MDetailSerie[];

    const füllungAuswahlwerteFürDetailSerie = filterPathIntersection(
      APP.metadata.Füllung_Auswahlwert,
      (p: PAuswahlwert) => p,
      detailSerieObjekte,
      (p: PDetailSerie) => p.Element.Füllung.Auswahlwert
    ) as MAuswahlwert[];

    return füllungAuswahlwerteFürDetailSerie;
  }

  /**
   * Setzt Profile auf senkrecht oder eben nicht, je nachdem ob die Leeroption (horizontal) gewählt wurde
   * Muss sich leider die Flügelbreiten merken und nacher wiederherstellen, da das beim change() nicht stabil bleibt.
   * @param senkrechtprofileAuswahlwert
   * @param element this.currentElement oder anderes (z.B. this.currentElementEinzeltor)
   * @private
   */
  private setSenkrechtprofile(
    senkrechtprofileAuswahlwert: MAuswahlwert,
    element = this.currentElement
  ) {
    const flügelBreitenVorher = element.getFlügelSizesV2().map((size) => size.x);

    let newMassAuswahlwertKey = element.massAuswahlwert.Key;
    if (senkrechtprofileAuswahlwert.Leeroption) {
      newMassAuswahlwertKey = newMassAuswahlwertKey.substringBefore("_Senkrecht");
    } else {
      newMassAuswahlwertKey = newMassAuswahlwertKey + "_Senkrecht";
    }

    const massAuswahlwert = APP.richMetadata.findAuswahlwert(newMassAuswahlwertKey);
    element.change({ massAuswahlwert, individualisiert: true });
    this.setEAntrieb(this.Workflow);
    flügelBreitenVorher.forEach((breite, i) => element.setFlügelBreite(breite, i));
    APP.dirty();
  }

  /**
   * Setzt die Position des Einzeltors bei Dreifachtoren, muss dafür Update3fachEinzeltor aufrufen
   * @param newAuswahlwert
   * @private
   */
  private setPosition_Einzeltor(newAuswahlwert: MAuswahlwert) {
    this.Position_Einzeltor = newAuswahlwert;
    this.update3fachEinzeltor();
    APP.dirty();
  }

  setAnMauer(newAuswahlwert?: MAuswahlwert) {
    this.AnMauer = newAuswahlwert || APP.metadata.AnMauer_Auswahlwert.find((x) => x.Leeroption);
    updateAnMauer(null);
  }

  /**
   * Gibt für ein gegebenes Element die passenden Masse zurück.
   * Passend = Füllung und Form kann das Element behalten
   * @param part Element (Standard: this.currentElement)
   */
  private getMassObjekte = (part = this.currentElement): MMass[] => {
    const elementMetasForm = filterPathIntersection(part.meta.Mass.Form, (p: PObjekt) =>
      elementOrTor(p.Mass)
    ) as MElement[];
    const masseMitFormFüllung = part.füllungAuswahlwert
      ? (filterPathIntersection(
          elementMetasForm,
          (p: PElement) => p.Mass,
          part.füllungAuswahlwert,
          (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt).Mass
        ) as MMass[])
      : [];
    return masseMitFormFüllung;
  };

  private getAnMauerAuswahlwerte() {
    if (!this.isAnMauerPossible())
      return APP.metadata.AnMauer_Auswahlwert.filter((x) => x.Leeroption);
    return APP.metadata.AnMauer_Auswahlwert;
  }

  /**
   * Gibt die passenden Auswahlwerte (Form, Füllung)  für Masse zurück
   * @param part Element (Standard: this.currentElement)
   */
  private getMassAuswahlwerte = (part = this.currentElement): MAuswahlwert[] => {
    const elementMetasForm = filterPathIntersection(part.meta.Mass.Form, (p: PObjekt) =>
      elementOrTor(p.Mass)
    );
    const massAuswahlwerteMitFormFüllung = part.füllungAuswahlwert
      ? (filterPathIntersection(
          elementMetasForm,
          (p: PTor) => p.Mass.Auswahlwert,
          part.füllungAuswahlwert,
          (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt).Mass.Auswahlwert
        ) as MAuswahlwert[])
      : [];
    return massAuswahlwerteMitFormFüllung;
  };

  /**
   * Setzt den Standardwert für gegebene Option
   * @param what Was soll gesetzt werden? "Workflow", "DetailSerie", "Füllung", ...
   */
  setDefault(what: String) {
    this.setAuswahlwert(what, defaultOf(this.getPassendeAuswahlwerte(what)));
  }

  getAusgewähleAuswahlwerte() {
    return Object.values(this.currentElement)
      .filter((x) => !isEmpty(x?.Auswahl))
      .sort((a, b) => (a.Auswahlbild ? 1 : -1));
  }

  /**
   * Gibt den EinbauAbstand zurück (links + rechts ohne Doppeltorlücke)
   */
  getEinbauAbstand(part = this.currentElement): number {
    return part.lichteWeite - part.breite;
  }

  /**
   * Gibt den Differenzwert von normaler Breite zur lichten Weite zurück (inkl. Doppeltorlücke)
   */
  getLichteWeiteZusatz(part = this.currentElement): number {
    if (!part) return 0;
    return (
      part.lichteWeite -
      part.breite -
      this.getAnschlagprofilZusatz(part) +
      (part.isDoppeltor ? APP.settings.doppeltorFlügelabstand : 0)
    );
  }

  /**
   * Gibt die Breite des Anschlagprofils zurück
   */
  getAnschlagprofilZusatz(part = this.currentElement): number {
    if (!part) return 0;
    return part.hasAnschlagprofil ? 0.015 : 0;
  }

  /**
   * Gibt den Differenzwert von normaler Breite zur Gesamtbreite zurück
   */
  getGesamtBreiteZusatz(): number {
    return (
      this.getLichteWeiteZusatz() +
      this.getPfostenBreiteSumme() +
      this.getLichteWeiteZusatz(this.currentElementEinzeltor)
    );
  }

  /**
   * Gibt den Differenzwert von normaler Breite zur Gesamtbreite zurück
   */
  getSchiebetorpufferZusatz(): number {
    return APP.settings.SchiebetorPuffer ?? 0.2;
  }

  /**
   * Gibt den Differenzwert von normaler Breite zur Gesamtbreite zurück
   */
  getSchiebetorFundamentüberstandZusatz(): number {
    return berechneFundament1Überstand(this.currentElement) ?? 0.525;
  }

  /**
   * Gibt den Überstand der E-Antriebe zurück (einfach, also nur auf einer Seite)
   */
  getEAntriebÜberstand(): number {
    return this.isDoppeltorEAntrieb() ? APP.settings.ÜberstandEAntrieb : 0;
  }

  /**
   * Eine Pfostenbreite
   */
  getPfostenBreite(): number {
    return _.first(APP.arrangement.getPfosten() as Pfosten[]).breite;
  }

  /**
   * Summe der Pfostenbreiten (gesamte Planung)
   */
  getPfostenBreiteSumme(): number {
    return APP.arrangement
      .getPfosten()
      .reduce((summe: number, pfosten: Pfosten) => summe + pfosten.breite, 0);
  }

  isShowMassAuswahl() {
    const workflow = this.getAuswahlwert("Workflow");
    const detailSerie = this.getAuswahlwert("DetailSerie");
    const füllung = this.getAuswahlwert("Füllung");

    const whitelisted = APP.metadata.Massauswahl_Whitelist.some((whitelist) => {
      return (
        (isEmpty(whitelist.Workflow) || whitelist.Workflow == workflow) &&
        (isEmpty(whitelist.DetailSerie) || whitelist.DetailSerie == detailSerie) &&
        (isEmpty(whitelist.Füllung) || _.includes(whitelist.Füllung, füllung))
      );
    });

    if (!whitelisted) return false;

    const massAuswahlwerte = this.getPassendeAuswahlwerte("Mass");
    return !isEmpty(massAuswahlwerte) && massAuswahlwerte.length > 1;
  }

  isDekorprofilAuswahl() {
    return this.currentElement.profilAuswahlwert != null;
  }

  isZaunelement() {
    return this.Workflow?.Key == "WORKFLOW_Zaunelement";
  }

  isEinzeltor() {
    return this.Workflow?.Key == "WORKFLOW_Einzeltor";
  }

  isDoppeltor() {
    return (
      this.Workflow?.Key == "WORKFLOW_Doppeltor" ||
      this.Workflow?.Key == "WORKFLOW_Doppeltor_EAntrieb"
    );
  }

  isDoppeltorEAntrieb() {
    return this.Workflow?.Key == "WORKFLOW_Doppeltor_EAntrieb";
  }

  isDreifachtor() {
    return this.Workflow?.Key == "WORKFLOW_Dreifachtor";
  }

  isSchiebetor() {
    return this.Workflow?.Key == "WORKFLOW_Schiebetor";
  }

  isTorWorkflow() {
    return this.isEinzeltor() || this.isDoppeltor() || this.isDreifachtor() || this.isSchiebetor();
  }

  isGlas() {
    return this.DetailSerie.Key == "SYSTEM GLAS";
  }

  isZaungitter() {
    return this.DetailSerie.Key == "LONGLIFE RIVA";
  }

  isSenkrechtprofileMöglich() {
    if (this.currentElement.isSenkrechtprofile) return true;

    const senkrechtForm = getSenkrechtFormVonNormalerForm(this.currentElement.form);
    if (isEmpty(senkrechtForm)) return false;

    const füllungAuswahlwert = this.getAuswahlwert("Füllung");
    const elementeMitSenkrechtFormUndFüllung = getElementMetasFormFüllung(
      getElementMetasGleicherForm(senkrechtForm),
      füllungAuswahlwert
    );
    return !isEmpty(elementeMitSenkrechtFormUndFüllung);
  }

  isAnMauerPossible() {
    if (this.isZaunelement() || this.isDoppeltorEAntrieb() || this.isDreifachtor()) return false;

    if (this.isDoppeltor()) {
      return (
        this.getMass(MASSE.BreiteStandflügel) - 0.001 < APP.settings.MaximalbreiteTorAnMauer &&
        this.getMass(MASSE.BreiteGehflügel) - 0.001 < APP.settings.MaximalbreiteTorAnMauer
      );
    } else {
      return this.getMass(MASSE.Breite) - 0.001 < APP.settings.MaximalbreiteTorAnMauer;
    }
  }

  isAnMauer() {
    if (!this.isAnMauerPossible()) return false;
    return this.AnMauer != null && !this.AnMauer.Leeroption;
  }

  isAnMauerBeide() {
    return this.isAnMauer() && this.AnMauer.Key == "AnMauer_beide";
  }

  isÖffnungNachInnen() {
    return this.currentElement.öffnungAuswahlwert.Key == "Öffnung_nach_innen";
  }

  /**
   * Kümmert sich darum, dass die vorgegebenen Url-Params auch eingestellt werden
   * Mögliche Parameter: workflow= (oder w=), serie= (oder s=), fuellung= (oder f=)
   * @param urlParams
   */
  handleUrlParams(urlParams: URLSearchParams) {
    let workflowKey = urlParams.get("workflow") || urlParams.get("w");
    if (workflowKey) {
      if (!workflowKey.startsWith("WORKFLOW")) workflowKey = "WORKFLOW_" + workflowKey;
      const workflow = APP.richMetadata.findAuswahlwert(workflowKey);
      if (workflow) this.setAuswahlwert("Workflow", workflow);
    }
    let serieKey = urlParams.get("serie") || urlParams.get("s");
    if (serieKey) {
      const serie = APP.richMetadata.findAuswahlwert(serieKey);
      if (serie) this.setAuswahlwert("DetailSerie", serie);
    }
    let füllungKey = urlParams.get("fuellung") || urlParams.get("f");
    if (füllungKey) {
      const füllung = APP.richMetadata.findAuswahlwert(füllungKey);
      if (füllung) this.setAuswahlwert("Füllung", füllung);
    }
  }

  toJSON() {
    return {
      Workflow: this.Workflow?.Key,
      DetailSerie: this.DetailSerie?.Key,
      Position_Einzeltor: this.Position_Einzeltor?.Key,
      AnMauer: this.AnMauer?.Key,
    };
  }

  fromJSON(json: any = {}) {
    this.Workflow = APP.richMetadata.findAuswahlwertOrDefault(json.Workflow, "Workflow");
    this.DetailSerie = APP.richMetadata.findAuswahlwertOrDefault(json.DetailSerie, "DetailSerie");
    this.Position_Einzeltor = APP.richMetadata.findAuswahlwertOrDefault(
      json.Position_Einzeltor,
      "Position_Einzeltor"
    );
    this.AnMauer = APP.richMetadata.findAuswahlwertOrDefault(json.AnMauer, "AnMauer");
  }
}
